import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, deleteField, doc, getDocs, orderBy, query, updateDoc, writeBatch } from "firebase/firestore";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useAuthState } from "react-firebase-hooks/auth";
import Modal from "react-modal";
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    Col,
    Container,
    Form,
    InputGroup,
    Offcanvas,
    Row,
    Spinner,
} from "react-bootstrap";
import { auth, db } from "../../firebase.init";
import "./RemedialProfit.css";

const LABOUR_MODAL_STYLES = {
    overlay: {
        backgroundColor: "rgba(15, 23, 42, 0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
    },
    content: {
        position: "relative",
        inset: "auto",
        top: "auto",
        left: "auto",
        right: "auto",
        bottom: "auto",
        maxWidth: "420px",
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        borderRadius: "1rem",
        border: "none",
        padding: 0,
        boxShadow: "0 20px 45px rgba(15, 23, 42, 0.25)",
    },
};

const CALENDAR_OWNER_ID = "rxaqnUdohkQBw3ZiH77vl2Eu4rT2";

const CURRENCY_FORMATTER = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
});

const DATE_FORMATTER = new Intl.DateTimeFormat("en-GB");
const FULL_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
});

const VIEW_MODES = [
    { key: "day", label: "Day" },
    { key: "week", label: "Week" },
    { key: "month", label: "Month" },
    { key: "year", label: "Year" },
];

const SORT_LABELS = {
    start: "Date",
    buildingAddress: "Address",
    jobPrice: "Job Price",
    materialCost: "Material",
    labourCost: "Labour",
    profit: "Profit",
    margin: "Margin",
};

const parseFromDatabase = (value) => {
    if (!value) return new Date(0);
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? new Date(0) : value;
    if (typeof value === "object" && typeof value.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date.getTime()) ? new Date(0) : date;
    }
    if (typeof value === "object" && "seconds" in value) {
        const date = new Date(value.seconds * 1000);
        return Number.isNaN(date.getTime()) ? new Date(0) : date;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        const britishDateMatch = trimmed.match(
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
        );
        if (britishDateMatch) {
            const [, day, month, year, hour = "0", minute = "0", second = "0"] = britishDateMatch;
            const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
            return Number.isNaN(date.getTime()) ? new Date(0) : date;
        }
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
    }
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? new Date(0) : fallback;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const startOfWeek = (date) => {
    const result = new Date(date);
    const day = result.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    result.setDate(result.getDate() + diff);
    return startOfDay(result);
};
const endOfWeek = (date) => {
    const start = startOfWeek(date);
    const result = new Date(start);
    result.setDate(start.getDate() + 6);
    return endOfDay(result);
};
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date) => new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const toNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (value === null || value === undefined || value === "") return 0;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    const number = Number.parseFloat(cleaned);
    return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value) => CURRENCY_FORMATTER.format(toNumber(value));
const formatPercent = (value) => `${toNumber(value).toFixed(1)}%`;
const safeText = (...values) => values.find((value) => String(value || "").trim()) || "";
const clampPercent = (value) => Math.max(0, Math.min(100, value));

const calculateRawProfit = (job) => {
    const jobPrice = toNumber(job.jobPrice);
    const materialCost = toNumber(job.materialCost);
    const labourCost = toNumber(job.labourCost);
    const profit = jobPrice - (materialCost + labourCost);
    const margin = jobPrice > 0 ? (profit / jobPrice) * 100 : 0;
    return { jobPrice, materialCost, labourCost, profit, margin };
};

const normalizeJob = (doc) => {
    const data = doc.data();
    const job = {
        id: doc.id,
        type: data.type || "general",
        start: parseFromDatabase(data.start),
        end: parseFromDatabase(data.end),
        buildingAddress: data.buildingAddress || "",
        buildingCity: data.buildingCity || "",
        postcode: data.postcode || "",
        assignedInspector: data.assignedInspector || "",
        assignedCarpenter: data.assignedCarpenter || "",
        assignedDirector: data.assignedDirector || "",
        inspectorName: safeText(data.inspectorName, data.assignedInspector),
        carpenterName: safeText(data.carpenterName, data.assignedCarpenter),
        directorName: safeText(data.directorName, data.assignedDirector),
        materialCost: data.materialCost || 0,
        jobPrice: data.jobPrice || 0,
        labourCost: toNumber(data.labourCost),
        carpenterCount: toNumber(data.carpenterCount) || 1,
        labourComments: data.labourComments || "",
    };

    return { ...job, ...calculateRawProfit(job) };
};


const computeEffectiveLabour = (jobs) => {
    const dayGroups = new Map();

    jobs.forEach((job) => {
        const key = job.start.toDateString();
        if (!dayGroups.has(key)) {
            dayGroups.set(key, { carpenterCount: 0, labourTotal: 0, count: 0 });
        }
        const group = dayGroups.get(key);
        group.carpenterCount = Math.max(group.carpenterCount, toNumber(job.carpenterCount));
        group.labourTotal = Math.max(group.labourTotal, toNumber(job.labourCost));
        group.count += 1;
    });

    return jobs.map((job) => {
        const key = job.start.toDateString();
        const group = dayGroups.get(key);
        const dayCarpenterCount = group.carpenterCount > 0 ? group.carpenterCount : 1;
        const dayEventCount = group.count > 0 ? group.count : 1;
        const dayLabourTotal = group.labourTotal;

        const dayLabourComments = job.labourComments || "";
        const effectiveLabourCost = dayLabourTotal / dayEventCount;

        const effectiveProfit = job.jobPrice - (job.materialCost + effectiveLabourCost);
        const effectiveMargin = job.jobPrice > 0 ? (effectiveProfit / job.jobPrice) * 100 : 0;

        return {
            ...job,
            dayCarpenterCount,
            dayEventCount,
            dayLabourTotal,
            dayLabourComments,
            effectiveLabourCost,
            effectiveProfit,
            effectiveMargin,
        };
    });
};

const getTotals = (jobsArr) =>
    jobsArr.reduce(
        (acc, job) => {
            acc.jobPrice += job.jobPrice;
            acc.materialCost += job.materialCost;
            acc.labourCost += job.effectiveLabourCost;
            acc.profit += job.effectiveProfit;
            acc.count += 1;
            return acc;
        },
        { jobPrice: 0, materialCost: 0, labourCost: 0, profit: 0, count: 0 }
    );

const getMargin = (totals) => (totals.jobPrice > 0 ? (totals.profit / totals.jobPrice) * 100 : 0);

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const downloadBlob = (content, filename, type = "text/csv;charset=utf-8") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

const Icon = {
    jobs: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="7" width="18" height="13" rx="2" />
            <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
    ),
    revenue: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.5" />
        </svg>
    ),
    material: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 8l-9-5-9 5 9 5 9-5z" />
            <path d="M3 8v8l9 5 9-5V8" />
            <path d="M12 13v8" />
        </svg>
    ),
    labour: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
    ),
    profitUp: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 6l-9.5 9.5-5-5L1 18" />
            <path d="M17 6h6v6" />
        </svg>
    ),
    profitDown: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 18l-9.5-9.5-5 5L1 6" />
            <path d="M17 18h6v-6" />
        </svg>
    ),
    percent: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="5" x2="5" y2="19" />
            <circle cx="6.5" cy="6.5" r="2.5" />
            <circle cx="17.5" cy="17.5" r="2.5" />
        </svg>
    ),
    download: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" />
        </svg>
    ),
    search: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
        </svg>
    ),
};

const MetricCard = ({ icon, value, label, tone = "neutral" }) => (
    <Card className={`metric-card ${tone} h-100 border-0 shadow-sm rounded-4`}>
        <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center">
            <div className="metric-icon mb-3">{icon}</div>
            <div className="metric-value fw-bold">{value}</div>
            <div className="metric-label text-muted small fw-semibold">{label}</div>
        </Card.Body>
    </Card>
);

const SortableHeader = ({ field, label, activeField, direction, onSort, align = "start" }) => {
    const isActive = activeField === field;
    const indicator = !isActive ? "↕" : direction === "asc" ? "↑" : "↓";

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSort(field);
        }
    };

    return (
        <th
            className={`profit-sortable ${align === "end" ? "text-end" : ""}`}
            onClick={() => onSort(field)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : "none"}
            title={`Sort by ${label}`}
        >
            {label} <span aria-hidden="true">{indicator}</span>
        </th>
    );
};

const CostGauge = ({ materialCost, labourCost, profit, jobPrice, size = "large" }) => {
    const safeMaterial = toNumber(materialCost);
    const safeLabour = toNumber(labourCost);
    const safeProfit = toNumber(profit);
    const safePrice = toNumber(jobPrice);
    const total = safeMaterial + safeLabour;
    const scale = Math.max(safePrice, total, 1);
    const isLoss = safeProfit < 0;
    const matPct = clampPercent((safeMaterial / scale) * 100);
    const labPct = clampPercent((safeLabour / scale) * 100);
    const profPct = isLoss ? 0 : clampPercent((safeProfit / scale) * 100);
    const markerPct = clampPercent((safePrice / scale) * 100);
    const overflow = isLoss ? clampPercent(100 - markerPct) : 0;

    return (
        <div className={`profit-gauge profit-gauge--${size}`} aria-label="Cost breakdown gauge">
            <div className="profit-gauge-track">
                <div className="profit-gauge-seg profit-gauge-seg--material" style={{ width: `${matPct}%` }} title={`Material: ${formatCurrency(safeMaterial)}`} />
                <div className="profit-gauge-seg profit-gauge-seg--labour" style={{ width: `${labPct}%` }} title={`Labour: ${formatCurrency(safeLabour)}`} />
                {!isLoss && <div className="profit-gauge-seg profit-gauge-seg--profit" style={{ width: `${profPct}%` }} title={`Profit: ${formatCurrency(safeProfit)}`} />}
                {isLoss && overflow > 0 && <div className="profit-gauge-overflow" style={{ left: `${markerPct}%`, width: `${overflow}%` }} title={`Loss: ${formatCurrency(Math.abs(safeProfit))}`} />}
                {size === "large" && <div className="profit-gauge-marker" style={{ left: `${markerPct}%` }}><span>Job Price</span></div>}
            </div>
        </div>
    );
};

const DetailRow = ({ label, value, valueClassName = "" }) => (
    <div className="d-flex justify-content-between align-items-center gap-3 py-2 border-bottom border-light-subtle">
        <span className="text-muted">{label}</span>
        <span className={`fw-semibold text-end ${valueClassName}`}>{value}</span>
    </div>
);

const RemedialProfit = () => {
    const [user, loadingAuth] = useAuthState(auth);
    const [rawJobs, setRawJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("month");
    const [anchorDate, setAnchorDate] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("start");
    const [sortDirection, setSortDirection] = useState("desc");
    const [selectedJob, setSelectedJob] = useState(null);
    const [showOffcanvas, setShowOffcanvas] = useState(false);

    const [isEditingJob, setIsEditingJob] = useState(false);
    const [editForm, setEditForm] = useState({ jobPrice: "", materialCost: "", labourComments: "" });
    const [savingJob, setSavingJob] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [showLabourModal, setShowLabourModal] = useState(false);
    const [labourModalDate, setLabourModalDate] = useState(formatDateInput(new Date()));
    const [labourForm, setLabourForm] = useState({ carpenterCount: "", labourCost: "", comments: "" });
    const [savingLabour, setSavingLabour] = useState(false);
    const [labourError, setLabourError] = useState(null);

    const jobs = useMemo(() => computeEffectiveLabour(rawJobs), [rawJobs]);

    const fetchRemedialJobs = useCallback(async () => {
        if (loadingAuth) return;
        if (!user?.uid) {
            setRawJobs([]);
            setLoading(false);
            setError("Please sign in to view remedial profit data.");
            return;
        }
        try {
            setError(null);
            setLoading(true);
            const eventsQuery = query(
                collection(db, "users", CALENDAR_OWNER_ID, "calendar"),
                orderBy("start", "asc")
            );
            const snapshot = await getDocs(eventsQuery);
            const fetched = snapshot.docs
                .map(normalizeJob)
                .filter((job) => String(job.type || "").toLowerCase() === "remedial");
            setRawJobs(fetched);
        } catch (err) {
            console.error("Failed to fetch remedial jobs:", err);
            setError("Failed to load remedial job data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [loadingAuth, user?.uid]);

    useEffect(() => {
        fetchRemedialJobs();
    }, [fetchRemedialJobs]);

    useEffect(() => {
        if (!selectedJob) return;
        const updated = jobs.find((j) => j.id === selectedJob.id);
        if (!updated) return;
        setSelectedJob(updated);
        if (!isEditingJob) {
            setEditForm({
                jobPrice: updated.jobPrice,
                materialCost: updated.materialCost,
                labourComments: updated.labourComments || updated.dayLabourComments || "",
            });
        }
    }, [jobs]);

    const labourModalJobs = useMemo(
        () => rawJobs.filter((j) => formatDateInput(j.start) === labourModalDate),
        [rawJobs, labourModalDate]
    );

    const { rangeStart, rangeEnd } = useMemo(() => {
        switch (viewMode) {
            case "day": return { rangeStart: startOfDay(anchorDate), rangeEnd: endOfDay(anchorDate) };
            case "week": return { rangeStart: startOfWeek(anchorDate), rangeEnd: endOfWeek(anchorDate) };
            case "year": return { rangeStart: startOfYear(anchorDate), rangeEnd: endOfYear(anchorDate) };
            default: return { rangeStart: startOfMonth(anchorDate), rangeEnd: endOfMonth(anchorDate) };
        }
    }, [viewMode, anchorDate]);

    const periodJobs = useMemo(
        () => jobs.filter((job) => job.start >= rangeStart && job.start <= rangeEnd),
        [jobs, rangeStart, rangeEnd]
    );

    const periodTotals = useMemo(() => getTotals(periodJobs), [periodJobs]);
    const periodMargin = useMemo(() => getMargin(periodTotals), [periodTotals]);

    const monthlyBreakdown = useMemo(() => {
        if (viewMode !== "year") return [];
        const months = Array.from({ length: 12 }, (_, i) => ({
            month: i,
            label: new Date(anchorDate.getFullYear(), i, 1).toLocaleString("en-GB", { month: "short" }),
            jobPrice: 0,
            materialCost: 0,
            labourCost: 0,
            profit: 0,
            count: 0,
        }));
        periodJobs.forEach((job) => {
            const m = job.start.getMonth();
            months[m].jobPrice += job.jobPrice;
            months[m].materialCost += job.materialCost;
            months[m].labourCost += job.effectiveLabourCost;
            months[m].profit += job.effectiveProfit;
            months[m].count += 1;
        });
        return months.map((m) => ({
            ...m,
            margin: m.jobPrice > 0 ? (m.profit / m.jobPrice) * 100 : 0,
        }));
    }, [periodJobs, viewMode, anchorDate]);

    const visibleJobs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return periodJobs;
        return periodJobs.filter((job) =>
            [
                job.buildingAddress,
                job.buildingCity,
                job.postcode,
                job.inspectorName,
                job.carpenterName,
                job.directorName,
                job.labourComments,
                job.dayLabourComments,
                DATE_FORMATTER.format(job.start),
                formatCurrency(job.jobPrice),
                formatCurrency(job.effectiveProfit),
                formatPercent(job.effectiveMargin),
            ]
                .join(" ")
                .toLowerCase()
                .includes(term)
        );
    }, [periodJobs, searchTerm]);

    const sortedJobs = useMemo(() => {
        return [...visibleJobs].sort((a, b) => {
            let valA, valB;
            if (sortField === "labourCost") {
                valA = a.effectiveLabourCost;
                valB = b.effectiveLabourCost;
            } else if (sortField === "profit") {
                valA = a.effectiveProfit;
                valB = b.effectiveProfit;
            } else if (sortField === "margin") {
                valA = a.effectiveMargin;
                valB = b.effectiveMargin;
            } else {
                valA = a[sortField];
                valB = b[sortField];
            }
            if (valA instanceof Date) valA = valA.getTime();
            if (valB instanceof Date) valB = valB.getTime();
            if (typeof valA === "string") {
                const cmp = String(valA || "").localeCompare(String(valB || ""), "en-GB", { sensitivity: "base", numeric: true });
                return sortDirection === "asc" ? cmp : -cmp;
            }
            const cmp = toNumber(valA) - toNumber(valB);
            return sortDirection === "asc" ? cmp : -cmp;
        });
    }, [visibleJobs, sortField, sortDirection]);

    const visibleTotals = useMemo(() => getTotals(sortedJobs), [sortedJobs]);
    const visibleMargin = useMemo(() => getMargin(visibleTotals), [visibleTotals]);

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection(field === "buildingAddress" ? "asc" : "desc");
        }
    };

    const navigate = (direction) => {
        setAnchorDate((prev) => {
            const next = new Date(prev);
            if (viewMode === "day") next.setDate(next.getDate() + direction);
            else if (viewMode === "week") next.setDate(next.getDate() + 7 * direction);
            else if (viewMode === "year") next.setFullYear(next.getFullYear() + direction);
            else next.setMonth(next.getMonth() + direction);
            return next;
        });
    };
    const goToToday = () => setAnchorDate(new Date());

    const headerLabel = useMemo(() => {
        switch (viewMode) {
            case "day": return FULL_DATE_FORMATTER.format(anchorDate);
            case "week": {
                const s = startOfWeek(anchorDate);
                const e = endOfWeek(anchorDate);
                return `${s.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
            }
            case "year": return `${anchorDate.getFullYear()}`;
            default: return anchorDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        }
    }, [viewMode, anchorDate]);

    const jumpPicker = () => {
        if (viewMode === "month") {
            const val = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, "0")}`;
            return (
                <Form.Control
                    type="month" size="sm"
                    className="profit-jump-input rounded-pill"
                    value={val}
                    aria-label="Jump to month"
                    onChange={(e) => {
                        const [y, m] = e.target.value.split("-");
                        setAnchorDate(new Date(Number(y), Number(m) - 1, 1));
                    }}
                />
            );
        }
        if (viewMode === "year") {
            return (
                <Form.Control
                    type="number" size="sm" min="2000" max="2100"
                    className="profit-jump-input profit-jump-input-year rounded-pill"
                    value={anchorDate.getFullYear()}
                    aria-label="Jump to year"
                    onChange={(e) => {
                        const y = Number(e.target.value);
                        if (Number.isFinite(y)) setAnchorDate(new Date(y, 0, 1));
                    }}
                />
            );
        }
        return (
            <Form.Control
                type="date" size="sm"
                className="profit-jump-input rounded-pill"
                value={formatDateInput(anchorDate)}
                aria-label="Jump to date"
                onChange={(e) => {
                    if (e.target.value) setAnchorDate(new Date(`${e.target.value}T00:00:00`));
                }}
            />
        );
    };

    const exportToCSV = () => {
        const headers = [
            "Date", "Address", "City", "Postcode", "Inspector", "Carpenter", "Director",
            "Job Price", "Material Cost", "Effective Labour", "Profit", "Margin %",
        ];
        const rows = sortedJobs.map((j) => [
            DATE_FORMATTER.format(j.start),
            j.buildingAddress, j.buildingCity, j.postcode,
            j.inspectorName, j.carpenterName, j.directorName,
            j.jobPrice.toFixed(2), j.materialCost.toFixed(2),
            j.effectiveLabourCost.toFixed(2),
            j.effectiveProfit.toFixed(2),
            j.effectiveMargin.toFixed(1),
        ]);
        const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
        downloadBlob(`\ufeff${csv}`, `remedial-profit-${viewMode}-${formatDateInput(anchorDate)}.csv`);
    };

    const metricTone = (val) => (toNumber(val) < 0 ? "critical" : "success");

    const handleRowClick = (job) => {
        setSelectedJob(job);
        setEditForm({
            jobPrice: job.jobPrice,
            materialCost: job.materialCost,
            labourComments: job.labourComments || job.dayLabourComments || "",
        });
        setIsEditingJob(false);
        setSaveError(null);
        setShowOffcanvas(true);
    };
    const handleRowKeyDown = (e, job) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRowClick(job);
        }
    };
    const closeOffcanvas = () => {
        setShowOffcanvas(false);
        setSelectedJob(null);
        setIsEditingJob(false);
        setSaveError(null);
    };

    const handleSaveJobDetails = async () => {
        if (!selectedJob) return;
        setSavingJob(true);
        setSaveError(null);
        try {
            const jobRef = doc(db, "users", CALENDAR_OWNER_ID, "calendar", selectedJob.id);
            const updatedLabourComments = String(editForm.labourComments || "").trim();

            await updateDoc(jobRef, {
                jobPrice: toNumber(editForm.jobPrice),
                materialCost: toNumber(editForm.materialCost),
                labourComments: updatedLabourComments,
            });

            setSelectedJob((prev) => ({
                ...prev,
                jobPrice: toNumber(editForm.jobPrice),
                materialCost: toNumber(editForm.materialCost),
                labourComments: updatedLabourComments,
            }));

            await fetchRemedialJobs();
            setIsEditingJob(false);
        } catch (err) {
            console.error("Failed to save job details:", err);
            setSaveError("Failed to save changes. Please try again.");
        } finally {
            setSavingJob(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingJob(false);
        setSaveError(null);
        if (selectedJob) {
            setEditForm({
                jobPrice: selectedJob.jobPrice,
                materialCost: selectedJob.materialCost,
                labourComments: selectedJob.labourComments || selectedJob.dayLabourComments || "",
            });
        }
    };

    const openLabourModal = (dateForModal) => {
        const dateKey = formatDateInput(dateForModal);
        setLabourModalDate(dateKey);
        const existing = jobs.find((j) => formatDateInput(j.start) === dateKey);
        setLabourForm({
            carpenterCount: existing ? existing.dayCarpenterCount : 1,
            labourCost: existing ? existing.dayLabourTotal : "",
            comments: existing ? existing.dayLabourComments : "",
        });
        setLabourError(null);
        setShowLabourModal(true);
    };

    const closeLabourModal = () => {
        setShowLabourModal(false);
        setLabourError(null);
    };

    const handleSaveLabour = async () => {
        if (labourModalJobs.length === 0) {
            setLabourError("No remedial events found on this date.");
            return;
        }
        setSavingLabour(true);
        setLabourError(null);
        try {
            const carpenterCount = Math.max(1, Math.trunc(toNumber(labourForm.carpenterCount)) || 1);
            const labourCost = Math.max(0, toNumber(labourForm.labourCost));
            const labourComments = String(labourForm.comments || "").trim();

            const batch = writeBatch(db);
            labourModalJobs.forEach((j) => {
                const ref = doc(db, "users", CALENDAR_OWNER_ID, "calendar", j.id);
                batch.update(ref, {
                    carpenterCount,
                    labourCost,
                    labourComments,
                    labourCostPerCarpenter: deleteField(),
                });
            });
            await batch.commit();
            await fetchRemedialJobs();
            setShowLabourModal(false);
        } catch (err) {
            console.error("Failed to save day labour:", err);
            setLabourError("Failed to save changes. Please try again.");
        } finally {
            setSavingLabour(false);
        }
    };

    const labourPreview = useMemo(() => {
        const dayTotal = Math.max(0, toNumber(labourForm.labourCost));
        const eventCount = labourModalJobs.length;
        const perEvent = eventCount > 0 ? dayTotal / eventCount : 0;
        return { dayTotal, perEvent, eventCount };
    }, [labourForm.labourCost, labourModalJobs.length]);

    if (loading || loadingAuth) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
                <div className="text-center">
                    <Spinner animation="border" variant="danger" />
                    <p className="mt-3 text-muted">Loading remedial job data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <span>{error}</span>
                    {user?.uid && (
                        <Button variant="outline-danger" size="sm" onClick={fetchRemedialJobs}>
                            Try again
                        </Button>
                    )}
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="service__section px-0 remedial-page">
            <div className="services__great__wrapper" id="services">
                <Container fluid className="profit-page-container py-4">
                    <Row className="justify-content-center g-4">
                        <Col xs={12}>
                            <div className="text-center mb-4">
                                <h1 className="header-title mt-3 mb-4" style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)" }}>
                                    Profit Analysis - Remedial Jobs
                                </h1>
                            </div>

                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <Card.Body
                                    className="d-flex justify-content-between align-items-center flex-wrap gap-3 pt-3 pb-3"
                                    style={{ minWidth: 0, overflow: "hidden" }}
                                >
                                    <ButtonGroup size="sm" className="profit-toggle-group" aria-label="Profit date range">
                                        {VIEW_MODES.map((view) => (
                                            <Button
                                                key={view.key}
                                                variant={viewMode === view.key ? "dark" : "outline-secondary"}
                                                onClick={() => setViewMode(view.key)}
                                                aria-pressed={viewMode === view.key}
                                            >
                                                {view.label}
                                            </Button>
                                        ))}
                                    </ButtonGroup>

                                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                        <Button
                                            variant="light"
                                            onClick={() => navigate(-1)}
                                            aria-label="Previous period"
                                            style={{
                                                width: 36,
                                                height: 36,
                                                minWidth: 36,
                                                padding: 0,
                                                borderRadius: "50%",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ display: "block" }}
                                            >
                                                <path d="M15 18l-6-6 6-6" />
                                            </svg>
                                        </Button>

                                        <span className="profit-period-label text-center fw-bold">{headerLabel}</span>

                                        <Button
                                            variant="light"
                                            onClick={() => navigate(1)}
                                            aria-label="Next period"
                                            style={{
                                                width: 36,
                                                height: 36,
                                                minWidth: 36,
                                                padding: 0,
                                                borderRadius: "50%",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ display: "block" }}
                                            >
                                                <path d="M9 18l6-6-6-6" />
                                            </svg>
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goToToday}>
                                            Today
                                        </Button>
                                    </div>

                                    <div
                                        className="profit-jump-control d-flex align-items-center gap-2 flex-nowrap"
                                        style={{
                                            maxWidth: "100%",
                                            flexShrink: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <span
                                            className="text-muted small profit-jump-label"
                                            style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                        >
                                            {"Jump\u00A0to"}
                                        </span>

                                        <div style={{ maxWidth: "160px", minWidth: 0, flexShrink: 1 }}>
                                            {jumpPicker()}
                                        </div>
                                    </div>

                                    {viewMode === "day" && (
                                        <Button
                                            variant="outline-dark"
                                            size="sm"
                                            className="rounded-pill"
                                            onClick={() => openLabourModal(anchorDate)}
                                        >
                                            Set Day Labour
                                        </Button>
                                    )}

                                    {/* <Button
                                        variant="outline-dark"
                                        size="sm"
                                        className="premium-action-btn d-flex align-items-center gap-2 rounded-pill"
                                        onClick={exportToCSV}
                                        disabled={sortedJobs.length === 0}
                                    >
                                        <Icon.download /> Export CSV
                                    </Button> */}
                                </Card.Body>
                            </Card>

                            <Row className="row-cols-2 row-cols-md-3 row-cols-lg-6 g-3 mb-4">
                                <Col><MetricCard icon={<Icon.jobs />} value={periodTotals.count} label="Jobs" /></Col>
                                <Col><MetricCard icon={<Icon.revenue />} value={formatCurrency(periodTotals.jobPrice)} label="Revenue" /></Col>
                                <Col><MetricCard icon={<Icon.material />} value={formatCurrency(periodTotals.materialCost)} label="Material" /></Col>
                                <Col><MetricCard icon={<Icon.labour />} value={formatCurrency(periodTotals.labourCost)} label="Labour" /></Col>
                                <Col><MetricCard
                                    icon={periodTotals.profit >= 0 ? <Icon.profitUp /> : <Icon.profitDown />}
                                    value={formatCurrency(periodTotals.profit)}
                                    label="Profit"
                                    tone={metricTone(periodTotals.profit)}
                                /></Col>
                                <Col><MetricCard
                                    icon={<Icon.percent />}
                                    value={formatPercent(periodMargin)}
                                    label="Margin"
                                    tone={metricTone(periodMargin)}
                                /></Col>
                            </Row>

                            <Card className="profit-gauge-card border-0 shadow-sm rounded-4 mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-3">
                                        <h5 className="profit-card-title mb-0">Cost Breakdown — {headerLabel}</h5>
                                        <span className="text-muted small">{formatCurrency(periodTotals.jobPrice)} total revenue</span>
                                    </div>
                                    <CostGauge
                                        materialCost={periodTotals.materialCost}
                                        labourCost={periodTotals.labourCost}
                                        profit={periodTotals.profit}
                                        jobPrice={periodTotals.jobPrice}
                                        size="large"
                                    />
                                    <div className="profit-legend d-flex flex-wrap gap-3 mt-3">
                                        <span><i className="profit-dot profit-dot--material" /> Material {formatCurrency(periodTotals.materialCost)}</span>
                                        <span><i className="profit-dot profit-dot--labour" /> Labour {formatCurrency(periodTotals.labourCost)}</span>
                                        <span>
                                            <i className={`profit-dot ${periodTotals.profit >= 0 ? "profit-dot--profit" : "profit-dot--loss"}`} />
                                            {periodTotals.profit >= 0 ? "Profit" : "Loss"} {formatCurrency(Math.abs(periodTotals.profit))}
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>

                            {viewMode === "year" && (
                                <Card className="profit-table-card border-0 shadow-sm rounded-4 mb-4">
                                    <Card.Header className="bg-white border-0 p-3">
                                        <h5 className="profit-card-title mb-0">Monthly Profit — {anchorDate.getFullYear()}</h5>
                                    </Card.Header>
                                    <Card.Body className="p-3 pt-0">
                                        <div style={{ height: 320 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={monthlyBreakdown} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                                                    <YAxis tickFormatter={(v) => `£${v}`} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                                                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #e5e7eb" }} />
                                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                                    <Bar dataKey="jobPrice" name="Job Price" fill="#374151" radius={[5, 5, 0, 0]} />
                                                    <Bar dataKey="materialCost" name="Material" fill="#94a3b8" radius={[5, 5, 0, 0]} />
                                                    <Bar dataKey="labourCost" name="Labour" fill="#cbd5e1" radius={[5, 5, 0, 0]} />
                                                    <Bar dataKey="profit" name="Profit" fill="#16a34a" radius={[5, 5, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card.Body>
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle profit-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-center">Month</th>
                                                    <th className="text-center">Jobs</th>
                                                    <th className="text-center">Job Price</th>
                                                    <th className="text-center">Material</th>
                                                    <th className="text-center">Labour</th>
                                                    <th className="text-center">Breakdown</th>
                                                    <th className="text-center">Profit</th>
                                                    <th className="text-center">Margin</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {monthlyBreakdown.map((m) => (
                                                    <tr key={m.month}>
                                                        <td className="text-center fw-semibold">{m.label}</td>
                                                        <td className="text-center">{m.count}</td>
                                                        <td className="text-center">{formatCurrency(m.jobPrice)}</td>
                                                        <td className="text-center text-muted">{formatCurrency(m.materialCost)}</td>
                                                        <td className="text-center text-muted">{formatCurrency(m.labourCost)}</td>
                                                        <td className="text-center"><CostGauge materialCost={m.materialCost} labourCost={m.labourCost} profit={m.profit} jobPrice={m.jobPrice} size="small" /></td>
                                                        <td className={`text-center fw-semibold profit-cell ${m.profit >= 0 ? 'profit-cell--positive' : 'profit-cell--negative'}`}>
                                                            {formatCurrency(m.profit)}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge rounded-pill ${m.margin >= 0 ? "text-bg-success" : "text-bg-danger"}`}>{formatPercent(m.margin)}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            )}

                            <Card className="profit-table-card border-0 shadow-sm rounded-4">
                                <Card.Header className="bg-white border-0 p-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h5 className="profit-card-title mb-0">
                                            Remedial Jobs <span className="text-muted fw-normal">({sortedJobs.length})</span>
                                        </h5>
                                        <InputGroup size="sm" className="profit-search">
                                            <InputGroup.Text className="bg-white border-end-0 rounded-start-pill"><Icon.search /></InputGroup.Text>
                                            <Form.Control
                                                className="border-start-0 rounded-end-pill"
                                                placeholder="Search address, team, amount…"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                aria-label="Search remedial jobs"
                                            />
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <div className="table-responsive profit-table-scroll">
                                    <table className="table table-hover align-middle profit-table mb-0">
                                        <thead>
                                            <tr>
                                                <SortableHeader field="start" label={SORT_LABELS.start} activeField={sortField} direction={sortDirection} onSort={toggleSort} />
                                                <SortableHeader field="buildingAddress" label={SORT_LABELS.buildingAddress} activeField={sortField} direction={sortDirection} onSort={toggleSort} />
                                                <th className="text-center">Carpenter</th>
                                                <SortableHeader field="jobPrice" label={SORT_LABELS.jobPrice} activeField={sortField} direction={sortDirection} onSort={toggleSort} align="center" />
                                                <SortableHeader field="materialCost" label={SORT_LABELS.materialCost} activeField={sortField} direction={sortDirection} onSort={toggleSort} align="center" />
                                                <SortableHeader field="labourCost" label={SORT_LABELS.labourCost} activeField={sortField} direction={sortDirection} onSort={toggleSort} align="center" />
                                                <th className="text-center">Breakdown</th>
                                                <SortableHeader field="profit" label={SORT_LABELS.profit} activeField={sortField} direction={sortDirection} onSort={toggleSort} align="center" />
                                                <SortableHeader field="margin" label={SORT_LABELS.margin} activeField={sortField} direction={sortDirection} onSort={toggleSort} align="center" />
                                                <th className="text-center">Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedJobs.length === 0 ? (
                                                <tr><td colSpan={12}><div className="profit-empty text-center py-5">No remedial jobs found</div></td></tr>
                                            ) : (
                                                sortedJobs.map((job) => (
                                                    <tr key={job.id}
                                                        onClick={() => handleRowClick(job)}
                                                        onKeyDown={(e) => handleRowKeyDown(e, job)}
                                                        role="button" tabIndex={0}
                                                        aria-label={`View breakdown for ${job.buildingAddress || "job"}`}
                                                    >
                                                        <td className="text-center">{DATE_FORMATTER.format(job.start)}</td>
                                                        <td className="text-center">
                                                            <div className="fw-semibold">{job.buildingAddress || "—"}</div>
                                                            {(job.buildingCity || job.postcode) && <div className="text-muted small">{[job.buildingCity, job.postcode].filter(Boolean).join(" · ")}</div>}
                                                        </td>
                                                        <td className="text-center">{job.carpenterName || "—"}</td>
                                                        <td className="text-center">{formatCurrency(job.jobPrice)}</td>
                                                        <td className="text-center text-muted">{formatCurrency(job.materialCost)}</td>
                                                        <td className="text-center text-muted" title={`Day labour ${formatCurrency(job.dayLabourTotal)} ÷ ${job.dayEventCount} event(s)`}>{formatCurrency(job.effectiveLabourCost)}</td>
                                                        <td className="text-center"><CostGauge materialCost={job.materialCost} labourCost={job.effectiveLabourCost} profit={job.effectiveProfit} jobPrice={job.jobPrice} size="small" /></td>
                                                        <td className={`text-center fw-semibold profit-cell ${job.effectiveProfit >= 0 ? 'profit-cell--positive' : 'profit-cell--negative'}`}>
                                                            {formatCurrency(job.effectiveProfit)}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge rounded-pill ${job.effectiveMargin >= 0 ? "text-bg-success" : "text-bg-danger"}`}>{formatPercent(job.effectiveMargin)}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            {(job.labourComments || job.dayLabourComments) ? (
                                                                <span
                                                                    className="d-inline-block text-truncate small text-muted"
                                                                    title={job.labourComments || job.dayLabourComments}
                                                                >
                                                                    {(job.labourComments || job.dayLabourComments).length > 20
                                                                        ? (job.labourComments || job.dayLabourComments).substring(0, 20).trim() + "..."
                                                                        : (job.labourComments || job.dayLabourComments)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        {sortedJobs.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={3} className="fw-semibold">{searchTerm.trim() ? "Visible Total" : "Total"}</td>
                                                    <td className="text-center fw-semibold">{formatCurrency(visibleTotals.jobPrice)}</td>
                                                    <td className="text-center">{formatCurrency(visibleTotals.materialCost)}</td>
                                                    <td className="text-center">{formatCurrency(visibleTotals.labourCost)}</td>
                                                    <td />
                                                    <td className={`text-center fw-semibold profit-cell ${visibleTotals.profit >= 0 ? 'profit-cell--positive' : 'profit-cell--negative'}`}>{formatCurrency(visibleTotals.profit)}</td>
                                                    <td className="text-center fw-semibold">{formatPercent(visibleMargin)}</td>
                                                    <td />
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Offcanvas
                show={showOffcanvas}
                onHide={closeOffcanvas}
                placement="end"
                className="profit-offcanvas remedial-page"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fw-bold">Job Breakdown</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {selectedJob && (
                        <>
                            <div className="mb-4">
                                <h6 className="text-uppercase text-muted small fw-bold">Address</h6>
                                <p className="mb-1 fw-semibold">{selectedJob.buildingAddress || "—"}</p>
                                {selectedJob.buildingCity && <p className="text-muted small mb-0">{selectedJob.buildingCity}</p>}
                                {selectedJob.postcode && <p className="text-muted small mb-0">{selectedJob.postcode}</p>}
                            </div>
                            <div className="mb-4">
                                <h6 className="text-uppercase text-muted small fw-bold">Date</h6>
                                <p>{FULL_DATE_FORMATTER.format(selectedJob.start)}</p>
                            </div>
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <Card.Body>
                                    <h6 className="text-uppercase text-muted small fw-bold mb-2">Team</h6>
                                    <DetailRow label="Carpenter" value={selectedJob.carpenterName || "—"} />
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="text-uppercase text-muted small fw-bold mb-0">Financials</h6>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge rounded-pill ${selectedJob.effectiveProfit >= 0 ? "text-bg-success" : "text-bg-danger"}`}>{selectedJob.effectiveProfit >= 0 ? "Profit" : "Loss"}</span>
                                            {!isEditingJob && (
                                                <Button size="sm" variant="outline-secondary" onClick={() => setIsEditingJob(true)}>
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {saveError && (
                                        <Alert variant="danger" className="py-2 small mb-3">{saveError}</Alert>
                                    )}

                                    {isEditingJob ? (
                                        <Form>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small text-muted mb-1">Job Price</Form.Label>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text>£</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.jobPrice}
                                                        onChange={(e) => setEditForm((f) => ({ ...f, jobPrice: e.target.value }))}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-2">
                                                <Form.Label className="small text-muted mb-1">Material Cost</Form.Label>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text>£</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.materialCost}
                                                        onChange={(e) => setEditForm((f) => ({ ...f, materialCost: e.target.value }))}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label className="small text-muted mb-1">Labour Comments</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    size="sm"
                                                    className="focus-red"
                                                    style={{ resize: "none" }}
                                                    placeholder="Add any notes about labour for this job..."
                                                    value={editForm.labourComments}
                                                    onChange={(e) => setEditForm((f) => ({ ...f, labourComments: e.target.value }))}
                                                />
                                            </Form.Group>

                                            <div className="d-flex gap-2">
                                                <Button size="sm" variant="dark" onClick={handleSaveJobDetails} disabled={savingJob}>
                                                    {savingJob ? <Spinner animation="border" size="sm" /> : "Save"}
                                                </Button>
                                                <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit} disabled={savingJob}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </Form>
                                    ) : (
                                        <>
                                            <DetailRow label="Job Price" value={formatCurrency(selectedJob.jobPrice)} />
                                            <DetailRow label="Material" value={formatCurrency(selectedJob.materialCost)} valueClassName="text-muted" />
                                            <div className="d-flex justify-content-between align-items-center gap-3 py-2 border-bottom border-light-subtle">
                                                <span className="text-muted">
                                                    Labour
                                                </span>
                                                <span className="fw-semibold text-end text-muted">{formatCurrency(selectedJob.effectiveLabourCost)}</span>
                                            </div>
                                            {/* {selectedJob.dayLabourComments && ( */}
                                            <div className="py-2 border-bottom border-light-subtle">
                                                <span className="d-block text-muted small mb-1">Labour comments</span>
                                                <span className="small" style={{ whiteSpace: "pre-wrap" }}>
                                                    {selectedJob.labourComments || selectedJob.dayLabourComments || "—"}
                                                </span>
                                            </div>
                                            {/* )} */}
                                            <div className="d-flex justify-content-between align-items-center gap-3 py-2 mt-1 border-top profit-sidebar-profit-row">
                                                <span className="fw-semibold">Profit</span>
                                                <span className="profit-sidebar-profit-value fw-bold fs-5 text-end"
                                                    style={{ color: selectedJob.effectiveProfit >= 0 ? '#16a34a' : '#b11111' }}>
                                                    {formatCurrency(selectedJob.effectiveProfit)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center gap-3 py-2">
                                                <span>Margin</span>
                                                <span className={`badge rounded-pill ${selectedJob.effectiveMargin >= 0 ? "text-bg-success" : "text-bg-danger"}`}>{formatPercent(selectedJob.effectiveMargin)}</span>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="profit-sidebar-breakdown-card border-0 shadow-sm rounded-4 mb-4">
                                <Card.Body>
                                    <h6 className="text-uppercase text-muted small fw-bold mb-3">Cost Breakdown</h6>
                                    <CostGauge
                                        materialCost={selectedJob.materialCost}
                                        labourCost={selectedJob.effectiveLabourCost}
                                        profit={selectedJob.effectiveProfit}
                                        jobPrice={selectedJob.jobPrice}
                                        size="large"
                                    />
                                    <Row className="g-2 mt-3">
                                        <Col xs={4}>
                                            <div className="profit-breakdown-mini p-2 rounded-3 bg-white border h-100">
                                                <span className="d-block text-muted text-uppercase small fw-bold">Material</span>
                                                <strong>{formatCurrency(selectedJob.materialCost)}</strong>
                                            </div>
                                        </Col>
                                        <Col xs={4}>
                                            <div className="profit-breakdown-mini p-2 rounded-3 bg-white border h-100">
                                                <span className="d-block text-muted text-uppercase small fw-bold">Labour</span>
                                                <strong>{formatCurrency(selectedJob.effectiveLabourCost)}</strong>
                                            </div>
                                        </Col>
                                        <Col xs={4}>
                                            <div className="profit-breakdown-mini p-2 rounded-3 bg-white border h-100">
                                                <span className="d-block text-muted text-uppercase small fw-bold">{selectedJob.effectiveProfit >= 0 ? "Profit" : "Loss"}</span>
                                                <strong className={selectedJob.effectiveProfit >= 0 ? "text-success" : "text-danger"}>{formatCurrency(Math.abs(selectedJob.effectiveProfit))}</strong>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            <Modal
                isOpen={showLabourModal}
                onRequestClose={closeLabourModal}
                style={LABOUR_MODAL_STYLES}
                ariaHideApp={false}
                contentLabel="Day Labour"
                shouldCloseOnOverlayClick={!savingLabour}
            >
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h6 className="fw-bold fs-6 mb-0">Day Labour</h6>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeLabourModal}
                        disabled={savingLabour}
                    />
                </div>

                <div className="p-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="small text-muted mb-1">Date</Form.Label>
                        <Form.Control
                            type="date"
                            size="sm"
                            className="focus-red"
                            value={labourModalDate}
                            onChange={(e) => {
                                const dateKey = e.target.value;
                                setLabourModalDate(dateKey);

                                const existing = jobs.find(
                                    (j) => formatDateInput(j.start) === dateKey
                                );

                                setLabourForm({
                                    carpenterCount: existing ? existing.dayCarpenterCount : 1,
                                    labourCost: existing ? existing.dayLabourTotal : "",
                                    comments: existing ? existing.dayLabourComments : "",
                                });

                                setLabourError(null);
                            }}
                        />
                    </Form.Group>

                    {labourError && <Alert variant="danger" className="py-2 small">{labourError}</Alert>}

                    <p className="text-muted small mb-2">
                        {labourModalJobs.length === 0
                            ? "No remedial events found on this date."
                            : `${labourModalJobs.length} remedial event${labourModalJobs.length === 1 ? "" : "s"} on this date.`}
                    </p>

                    <Row className="g-2 mb-3">
                        <Col xs={6}>
                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">Carpenters</Form.Label>
                                <InputGroup size="sm">
                                    <InputGroup.Text>Qty</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={labourForm.carpenterCount}
                                        onChange={(e) =>
                                            setLabourForm((f) => ({ ...f, carpenterCount: e.target.value }))
                                        }
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col xs={6}>
                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">Total labour cost</Form.Label>
                                <InputGroup size="sm">
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={labourForm.labourCost}
                                        onChange={(e) =>
                                            setLabourForm((f) => ({ ...f, labourCost: e.target.value }))
                                        }
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* <Form.Group className="mb-3">
                        <Form.Label className="small text-muted mb-1">
                            Comments
                        </Form.Label>

                        <InputGroup size="sm">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ maxHeight: "100px", resize: "none", }}
                                placeholder="Add any notes about this day's labour..."
                                value={labourForm.comments}
                                onChange={(e) =>
                                    setLabourForm((f) => ({
                                        ...f,
                                        comments: e.target.value,
                                    }))
                                }
                            />
                        </InputGroup>
                    </Form.Group> */}

                    <div className="p-3 rounded-3 bg-light">
                        <div className="d-flex justify-content-between small text-muted mb-1">
                            <span>Total labour cost</span>
                            <span>{formatCurrency(labourPreview.dayTotal)}</span>
                        </div>
                        <div className="d-flex justify-content-between fw-semibold">
                            <span>Labour per event ({labourPreview.eventCount || 0})</span>
                            <span>{formatCurrency(labourPreview.perEvent)}</span>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 p-3 border-top">
                    <Button variant="outline-secondary" size="sm" onClick={closeLabourModal} disabled={savingLabour}>
                        Cancel
                    </Button>
                    <Button
                        variant="dark"
                        size="sm"
                        onClick={handleSaveLabour}
                        disabled={savingLabour || labourModalJobs.length === 0}
                    >
                        {savingLabour ? <Spinner animation="border" size="sm" /> : "Save"}
                    </Button>
                </div>
            </Modal>
        </Container>
    );
};

export default RemedialProfit;